import { publishEvent } from 'serviceWorker/broadcasts/publishers';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { ProjectModel } from 'shared/domain/project/types/model';
import { ENTITY_SERVICE_SYNC_DEBOUNCE_MS } from 'serviceWorker/const/syncTiming';
import { swSignals } from 'serviceWorker/events/swSignals';
import { readProjectModels } from 'serviceWorker/broadcasts/project/all';
import { projectSource } from 'serviceWorker/services/projects/projectSource';
import * as projectsService from 'serviceWorker/db/projects/projectsService';
import * as selectedProjectRepository from 'serviceWorker/db/selectedProject';
import * as config from 'serviceWorker/db/config';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { debounce } from 'serviceWorker/helpers/debounce';
import { findById } from 'shared/utils/id/findById';
import { clearProjectData } from 'serviceWorker/services/dataClearer';
import { applySelectedProjectToConfig } from 'serviceWorker/services/config/applyServiceWorkerConfig';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import { selectedProjectContract } from 'shared/domain/selectedProject/selectedProjectContract';

/**
 * Short on purpose: the resync broadcast after a list download must not wait.
 * On timeout we return [] so the selection is not cleared.
 */
const PROJECT_LIST_WAIT_TIMEOUT_MS = 2000;

function sendSelectedProject(
  selectedProject: ProjectModel | undefined
): void {
  publishEvent(selectedProjectContract, DomainMessagesTypes.selectedProject, {
    project: selectedProject,
  });
}

/** Broadcasts the selected project again after the project list changed (its metadata, other tabs). */
const getSelectedProjectHandler = async (): Promise<void> => {
  const [selectedProjectIdentificable, projects] = await Promise.all([
    selectedProjectRepository.get(),
    getProjects(),
  ]);

  if (!selectedProjectIdentificable) {
    return sendSelectedProject(undefined);
  }

  const selectedProject = projects.find(
    (project) => project._id === selectedProjectIdentificable._id
  );
  if (!selectedProject) {
    if (projects.length > 0) {
      // Selected project is missing from a complete list — clear it.
      selectedProjectRepository.clear();
      return sendSelectedProject(undefined);
    }
    // Empty list = timeout / still downloading. Keep IDB selection and do
    // not broadcast: an empty selection would reset the pages' project.
    return;
  }

  return sendSelectedProject(selectedProject);
};

/**
 * The project of the list, `undefined` when it is not there. The selection is
 * cleared only when a non-empty list lacks it: an empty one comes from a
 * failed download (the pull cleared the table), as in `getSelectedProjectHandler`.
 */
async function findInProjectList(projectId: string, context: string): Promise<ProjectModel | undefined> {
  const projects = await projectSource.getAll();
  const project = findById(projects, projectId, context);
  if (!project && projects.length > 0) {
    await selectedProjectRepository.clear();
  }
  return project;
}

/**
 * The selected project once its data can be read, `null` when none is
 * selected or it cannot be found. Waits for a missing project list instead of
 * guessing from an empty one.
 */
export async function readSelectedProject(): Promise<ProjectModel | null> {
  const selection = await selectedProjectRepository.get();
  if (!selection) {
    return null;
  }
  return (await findInProjectList(selection._id, 'selected project')) ?? null;
}

/** The project is selected and the config points at it: nothing of another project to abort or clear. */
async function isSelectedAlready(projectId: string): Promise<boolean> {
  const [selection, configProjectId] = await Promise.all([selectedProjectRepository.get(), config.getProjectId()]);
  return areObjectIdsEqual(selection?._id, projectId) && areObjectIdsEqual(configProjectId, projectId);
}

/**
 * Switches to the project: stores the selection and points the config at it
 * (the downloads it starts call the API for that project), aborts what runs
 * for the previous project, clears its data once with requests suspended, and
 * broadcasts it (other tabs, the IndexedDB subscriptions). Resolves once the
 * data is cleared and requests run again, so downloads of the new project
 * start on empty tables; rejects when the project is not in the user's list.
 * The project selected already is only broadcast: its data stays.
 */
export async function selectProject(projectId: string): Promise<ProjectModel> {
  const project = await findInProjectList(projectId, 'project to select');
  if (!project) {
    throw new Error(`Project ${projectId} is not among the projects of the user`);
  }
  if (await isSelectedAlready(project._id)) {
    // Choosing the project again (a picker, an automatic selection at start) keeps its data and its downloads.
    sendSelectedProject(project);
    return project;
  }

  await selectedProjectRepository.set({ _id: project._id });
  // Aborted and outdated (dataScope) before the config changes: nothing of the previous project runs with the new one.
  swSignals.projectDataReplaced.emit();
  await applySelectedProjectToConfig(project);
  // One flush, awaited: a second, unawaited one would wipe the new project's first downloads.
  await clearProjectData();
  sendSelectedProject(project);
  return project;
}

export async function getProjects(): Promise<ProjectModel[]> {
  const projectsStatus = await projectsService.get();

  if (!projectsStatus?.isDownloaded || projectsStatus?.isDownloading) {
    return waitForProjectList();
  }

  return readProjectModels();
}

/**
 * The project list once downloaded (a missing download is started), or none
 * after a short wait; the wait ends as soon as the list comes.
 */
function waitForProjectList(): Promise<ProjectModel[]> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      swLog(`Project list not downloaded within ${PROJECT_LIST_WAIT_TIMEOUT_MS}ms`, LogLevel.INFO, null, null);
      resolve([]);
    }, PROJECT_LIST_WAIT_TIMEOUT_MS);

    projectSource
      .getAll()
      .catch((): ProjectModel[] => [])
      .then((projects) => {
        clearTimeout(timeoutId);
        resolve(projects);
      });
  });
}

/** Sends the selected project again (debounced), e.g. after the project list was downloaded. */
export const resyncSelectedProject = debounce(getSelectedProjectHandler, ENTITY_SERVICE_SYNC_DEBOUNCE_MS);

/** Publishes the selected project again once a complete list of projects is stored. */
export function init(): void {
  swSignals.projectListStored.subscribe(resyncSelectedProject);
}
